'use client'

import { useMemo } from 'react'
import { Briefcase, MapPin } from 'lucide-react'

import { EntityMetadataRow } from '@/components/entity-metadata-row'
import {
  EntityIdentityCard,
  EntityIdentityCardBody,
  EntityIdentityCardMedia,
  EntityIdentityCardMetadata,
  EntityIdentityCardTitle,
} from '@/components/entity-identity-card'
import { getProfileInitials } from '@/components/profile/profile-initials'
import { ProfileSkillsSection } from '@/components/profile/profile-section-skills-card'
import {
  ProfileCertificationsSection,
  ProfileEducationSection,
  ProfileExperienceSection,
  ProfilePapersSection,
} from '@/components/profile/profile-section-work-cards'
import { EntityAboutSection } from '@/components/ui/entity-about-section'
import { PersonContactSection } from '@/components/ui/entity-contact-sections'
import { extraSocialContactFrom } from '@/lib/social-networks'
import { Card, CardContent } from '@/components/ui/card'
import { InitialsAvatar } from '@/components/ui/initials-avatar'
import { candidateSections, type ProfileData } from '@/hooks/use-profile'
import { getPersonDisplayMeta } from '@/lib/person-identity-display'
import { cn } from '@/lib/utils'
import { OpenToWorkBadge } from '@/components/profile/open-to-work-badge'
import { formatEntityLocation } from '@/utils/format-entity-location'
import { formatProfileEvidenceDateRange } from '@/lib/profile-evidence-display'
import {
  sortDemonstratedSkillSummaries,
  splitClaimedOnlySkills,
} from '@/utils/profile-skills-display'
import { PublicProfileAboutSkillsSection } from '@/components/public/public-profile-about-skills'

export type PersonProfileStandingViewProps = {
  profile: ProfileData
  /** When false, omit name from the identity card (page header may already show it). */
  showIdentityName?: boolean
  /** When false, omit overlapping avatar hero (e.g. employer person page). */
  showAvatar?: boolean
  skillsTitle?: string
  skillsDescription?: string
  /** Public share page: top 10 skills, no level groups. */
  skillsVariant?: 'full' | 'public'
  className?: string
}

/** Read-only profile standing document — same sections and order as the candidate Profile page. */
export function PersonProfileStandingView({
  profile,
  showIdentityName = true,
  showAvatar = true,
  skillsTitle = 'Skills',
  skillsDescription,
  className,
}: PersonProfileStandingViewProps) {
  const candidate = candidateSections(profile)
  const displayName = getPersonDisplayMeta(profile).formalName || 'Candidate'
  const locationLine = formatEntityLocation(profile.city, profile.country)

  const openToWorkBadge = candidate.open_to_work ? <OpenToWorkBadge /> : null

  const identityMetadata = (
    <EntityIdentityCardMetadata>
      {!showIdentityName && openToWorkBadge ? openToWorkBadge : null}
      {profile.headline ? (
        <EntityMetadataRow icon={Briefcase}>
          <span>{profile.headline}</span>
        </EntityMetadataRow>
      ) : null}
      {locationLine ? (
        <EntityMetadataRow icon={MapPin}>
          <span>{locationLine}</span>
        </EntityMetadataRow>
      ) : null}
      <PersonContactSection
        email={profile.email}
        phone={profile.phone}
        website={profile.website}
        linkedin_url={profile.linkedin_url}
        x_url={profile.x_url}
        {...extraSocialContactFrom(profile)}
      />
    </EntityIdentityCardMetadata>
  )

  return (
    <div className={cn('space-y-6', className)}>
      {showAvatar ? (
        <EntityIdentityCard
          coverEnabled={false}
          media={
            <EntityIdentityCardMedia variant="avatar">
              <InitialsAvatar
                src={profile.avatar_url ?? undefined}
                colorSeed={
                  getProfileInitials({ full_name: profile.full_name ?? undefined }).colorSeed
                }
                framed
                className="size-[120px]"
                alt=""
              />
            </EntityIdentityCardMedia>
          }
        >
          <EntityIdentityCardBody>
            {showIdentityName ? (
              <EntityIdentityCardTitle badge={openToWorkBadge}>{displayName}</EntityIdentityCardTitle>
            ) : null}
            {identityMetadata}
          </EntityIdentityCardBody>
        </EntityIdentityCard>
      ) : (
        <Card>
          <CardContent className="space-y-4">
            {showIdentityName ? (
              <EntityIdentityCardTitle badge={openToWorkBadge}>{displayName}</EntityIdentityCardTitle>
            ) : null}
            {identityMetadata}
          </CardContent>
        </Card>
      )}

      <PersonProfileStandingSections
        profile={profile}
        skillsTitle={skillsTitle}
        skillsDescription={skillsDescription}
      />
    </div>
  )
}

/** About + skills + evidence sections shared by logged-in standing views and public `/p/:slug`. */
export function PersonProfileStandingSections({
  profile,
  skillsTitle = 'Skills',
  skillsDescription,
  skillsVariant = 'full',
}: Pick<PersonProfileStandingViewProps, 'profile' | 'skillsTitle' | 'skillsDescription' | 'skillsVariant'>) {
  const candidate = candidateSections(profile)
  const demonstratedSkillSummaries = useMemo(
    () => sortDemonstratedSkillSummaries(candidate.skill_summaries ?? []),
    [candidate.skill_summaries],
  )

  const claimedOnlySkills = useMemo(
    () => splitClaimedOnlySkills(candidate.skills ?? [], candidate.skill_summaries),
    [candidate.skills, candidate.skill_summaries],
  )

  return (
    <>
      {skillsVariant === 'public' ? (
        <PublicProfileAboutSkillsSection
          bio={profile.bio}
          demonstratedSkillSummaries={demonstratedSkillSummaries}
        />
      ) : (
        <>
          <EntityAboutSection body={profile.bio} emptyVariant="hide" />

          <ProfileSkillsSection
            readOnly
            skillsHighlighted={false}
            profileId={profile.id}
            demonstratedSkillSummaries={demonstratedSkillSummaries}
            claimedOnlySkills={claimedOnlySkills}
            experiences={candidate.experiences}
            education={candidate.education}
            title={skillsTitle}
            description={skillsDescription}
          />
        </>
      )}

      <ProfileExperienceSection profile={profile} readOnly />

      <ProfilePapersSection profile={profile} readOnly />

      <ProfileEducationSection
        profile={profile}
        educationHighlighted={false}
        formatDateRange={formatProfileEvidenceDateRange}
        readOnly
      />

      <ProfileCertificationsSection profile={profile} certificationsHighlighted={false} readOnly />
    </>
  )
}
